import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    DataTable,
    Heading,
    Layer,
    Page,
    PageContent,
    Select,
    Text,
    TextArea,
    TextInput,
} from 'grommet';
import { useOutletContext } from 'react-router-dom';
import { InfoSessionService } from '@enrich/shared-lib';

const SESSION_TYPE_OPTIONS = [
    { label: 'Info Session', value: 'info_session' },
    { label: 'Zoom Call', value: 'zoom_call' },
];

const DURATION_OPTIONS = [
    { label: '30 minutes', value: 30 },
    { label: '45 minutes', value: 45 },
    { label: '60 minutes', value: 60 },
    { label: '90 minutes', value: 90 },
];

const service = new InfoSessionService();

export const InfoSessions = () => {
    const user = useOutletContext();
    const company = user?.company;

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editSession, setEditSession] = useState(null);
    const [error, setError] = useState('');

    const loadSessions = useCallback(async () => {
        if (!company?.id) return;
        setLoading(true);
        try {
            const data = await service.getCompanySessions(company.id);
            setSessions(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [company?.id]);

    useEffect(() => {
        loadSessions();
    }, [loadSessions]);

    const handleCreate = () => {
        setEditSession(null);
        setError('');
        setShowModal(true);
    };

    const handleEdit = (session) => {
        setEditSession(session);
        setError('');
        setShowModal(true);
    };

    const handleDelete = async (sessionId) => {
        try {
            await service.deleteSession(sessionId);
            setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        } catch (err) {
            console.error(err);
        }
    };

    const handleSave = async (formData) => {
        try {
            if (editSession) {
                await service.updateSession(editSession.id, formData);
            } else {
                await service.createSession(company.id, formData);
            }
            setShowModal(false);
            loadSessions();
        } catch (err) {
            setError(err.message || 'Failed to save session.');
        }
    };

    const formatSessionType = (type) =>
        SESSION_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

    const formatDuration = (minutes) =>
        minutes ? `${minutes} min` : '';

    const formatDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box direction="row" justify="between" align="center">
                    <Heading level="3" margin="none">
                        Sessions
                    </Heading>
                    <Button
                        primary
                        label="Create Session"
                        onClick={handleCreate}
                    />
                </Box>

                {loading ? (
                    <Text>Loading...</Text>
                ) : sessions.length === 0 ? (
                    <Box
                        pad="medium"
                        elevation="small"
                        round="small"
                        align="center">
                        <Text color="enrich-light-grey">
                            No sessions scheduled yet.
                        </Text>
                    </Box>
                ) : (
                    <DataTable
                        columns={[
                            {
                                property: 'title',
                                header: 'Title',
                                primary: true,
                            },
                            {
                                property: 'sessionType',
                                header: 'Type',
                                render: (row) =>
                                    formatSessionType(row.sessionType),
                            },
                            {
                                property: 'sessionTime',
                                header: 'Date & Time',
                                render: (row) =>
                                    formatDateTime(row.sessionTime),
                            },
                            {
                                property: 'duration',
                                header: 'Duration',
                                render: (row) => formatDuration(row.duration),
                            },
                            {
                                property: 'joinUrl',
                                header: 'Join URL',
                                render: (row) =>
                                    row.joinUrl ? (
                                        <a
                                            href={row.joinUrl}
                                            target="_blank"
                                            rel="noopener noreferrer">
                                            Join
                                        </a>
                                    ) : null,
                            },
                            {
                                property: 'actions',
                                header: '',
                                render: (row) => (
                                    <Box direction="row" gap="small">
                                        <Button
                                            size="small"
                                            label="Edit"
                                            onClick={() => handleEdit(row)}
                                        />
                                        <Button
                                            size="small"
                                            label="Delete"
                                            color="enrich-red"
                                            onClick={() =>
                                                handleDelete(row.id)
                                            }
                                        />
                                    </Box>
                                ),
                            },
                        ]}
                        data={sessions}
                    />
                )}
            </PageContent>

            {showModal && (
                <SessionFormModal
                    session={editSession}
                    error={error}
                    onSave={handleSave}
                    onClose={() => setShowModal(false)}
                />
            )}
        </Page>
    );
};

const EMPTY_FORM = {
    title: '',
    sessionType: 'info_session',
    sessionTime: '',
    duration: 60,
    joinUrl: '',
    description: '',
};

const SessionFormModal = ({ session, error, onSave, onClose }) => {
    const isEdit = !!session;
    const [form, setForm] = useState(
        isEdit
            ? {
                  title: session.title ?? '',
                  sessionType: session.sessionType ?? 'info_session',
                  sessionTime: session.sessionTime
                      ? session.sessionTime.slice(0, 16)
                      : '',
                  duration: session.duration ?? 60,
                  joinUrl: session.joinUrl ?? '',
                  description: session.description ?? '',
              }
            : { ...EMPTY_FORM },
    );
    const [busy, setBusy] = useState(false);

    const set = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSubmit = async () => {
        setBusy(true);
        await onSave({
            title: form.title,
            sessionType: form.sessionType,
            sessionTime: form.sessionTime
                ? new Date(form.sessionTime).toISOString()
                : null,
            duration: form.duration ? Number(form.duration) : null,
            joinUrl: form.joinUrl,
            description: form.description || null,
        });
        setBusy(false);
    };

    return (
        <Layer
            onClickOutside={onClose}
            onEsc={onClose}
            position="center">
            <Box
                background="white"
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                width="medium"
                gap="small">
                <Heading level="3" margin="none">
                    {isEdit ? 'Edit Session' : 'Create Session'}
                </Heading>

                <Box gap="small">
                    <FormField label="Title">
                        <TextInput
                            placeholder="e.g. Company Info Session Q2"
                            value={form.title}
                            onChange={(e) => set('title', e.target.value)}
                        />
                    </FormField>

                    <FormField label="Type">
                        <Select
                            options={SESSION_TYPE_OPTIONS}
                            labelKey="label"
                            valueKey={{ key: 'value', reduce: true }}
                            value={form.sessionType}
                            onChange={({ value }) =>
                                set('sessionType', value)
                            }
                        />
                    </FormField>

                    <FormField label="Date & Time">
                        <TextInput
                            type="datetime-local"
                            value={form.sessionTime}
                            onChange={(e) => set('sessionTime', e.target.value)}
                        />
                    </FormField>

                    <FormField label="Duration">
                        <Select
                            options={DURATION_OPTIONS}
                            labelKey="label"
                            valueKey={{ key: 'value', reduce: true }}
                            value={form.duration}
                            onChange={({ value }) => set('duration', value)}
                        />
                    </FormField>

                    <FormField label="Zoom / Join URL">
                        <TextInput
                            placeholder="https://zoom.us/j/..."
                            value={form.joinUrl}
                            onChange={(e) => set('joinUrl', e.target.value)}
                        />
                    </FormField>

                    <FormField label="Description (optional)">
                        <TextArea
                            placeholder="What should attendees expect?"
                            value={form.description}
                            onChange={(e) =>
                                set('description', e.target.value)
                            }
                            rows={3}
                        />
                    </FormField>
                </Box>

                {error ? <Text color="enrich-red">{error}</Text> : null}

                <Box direction="row" justify="end" gap="small">
                    <Button label="Cancel" onClick={onClose} />
                    <Button
                        primary
                        label={isEdit ? 'Save' : 'Create'}
                        onClick={handleSubmit}
                        busy={busy}
                        disabled={!form.title || !form.sessionTime || !form.joinUrl}
                    />
                </Box>
            </Box>
        </Layer>
    );
};

const FormField = ({ label, children }) => (
    <Box gap="xsmall">
        <Text size="small" weight="bold">
            {label}
        </Text>
        {children}
    </Box>
);
